import { apiClient } from './api';
import { Control, NamedTag } from '../types';
import type { FilterableFarmProfile, FilterRule } from '../types/farm-profile';
import type { ControlPeriod, CoverageRule } from '@shared/control-period';
import type { LinkReason } from '@shared/document-validity';
import type { FieldDocument } from '../utils/controls/indexFieldDocuments';
import { API_BASE_URL } from '../constants';

export interface Share {
  id: string;
  recipientEmail: string;
  accessLevel: 'VIEW' | 'DOWNLOAD' | 'COMMENT';
  accessToken: string;
  expiresAt: string;
  isActive: boolean;
  createdAt: string;
  lastAccessed: string | null;
  accessCount: number;
  shareUrl: string;
  plainPassword?: string; // Only included if password protection is enabled
  requiresPassword: boolean; // Indicates if password is required for access
  control: Control;
}

export interface CreateShareRequest {
  controlId: string;
  recipientEmail: string;
  accessLevel: 'VIEW' | 'DOWNLOAD' | 'COMMENT';
  expiresInHours: number;
  requirePassword?: boolean; // Optional - defaults to true (password enabled)
}

export interface ShareResponse {
  success: boolean;
  data: Share;
}

export interface SharesResponse {
  success: boolean;
  data: Share[];
}

export interface PublicShareData {
  control: {
    id: string;
    name: string;
    description: string | null;
    controlType: {
      id: string;
      name: string;
      issuer: {
        id: string;
        name: string;
        displayName: string;
      };
    };
    status: string;
    /** Planned control date (ISO), or null when the farmer has not set one. */
    scheduledDate: string | null;
    /** IDs of requirements the farmer has manually activated despite being filtered by farm profile. */
    activatedRequirementIds?: string[];
    /**
     * The document links for this control — Prisma's join rows, passed through as-is.
     *
     * The join-row scalars are declared alongside the nested document because the
     * share page shapes requirement documents straight off these rows. `matchedAt`
     * in particular drives the "tillagd i kontrollen" date, and a field missing here
     * must be a compile error rather than a silently blank date on the page.
     *
     * The nested document is deliberately narrower than the Document model: this
     * endpoint is unauthenticated, so the repository selects only these fields.
     * See ShareRepository.getControlForShare.
     */
    controlDocuments: Array<{
      /** When the document was attached to this control. Drives the attach date. */
      matchedAt: string;
      /** Set when the link fulfils a document requirement. */
      requirementId: string | null;
      /** Set instead of requirementId when the link answers a form field. */
      fieldId: string | null;
      /** Why the link was made automatically (AG-253); null for manual links. */
      linkReason?: LinkReason | null;
      document: {
        id: string;
        filename: string;
        originalName: string;
        fileSize: number;
        mimeType: string;
        uploadedAt: string;
        /**
         * Named rather than bare slugs: this endpoint is unauthenticated, so the
         * page cannot fetch the vocabulary to resolve them. ShareService resolves
         * them once for the whole payload.
         */
        tags: NamedTag[];
        /** The document's tag sets, named like `tags` (their union). */
        tagSets: NamedTag[][];
        /** Narrowed to what this page reads; the endpoint sends more of the row. */
        metadata: {
          validToDate: string | null;
          validFromDate: string | null;
          /** Explicit reporting years (AG-259); year label on the document row. */
          coveredYears: number[];
          /** The mode the farmer chose; null on rows that predate it (AG-298). */
          validityMode: string | null;
        } | null;
      };
    }>;
    documentRequirements: Array<{
      id: string;
      /** Tag slugs the requirement asks for; a document matches on superset. */
      tags: string[];
      description: string | null;
      informationText: string | null;
      isRequired: boolean;
      validityMode: string | null;
      /** Months a document stays valid; 'expires' mode only. */
      validityPeriod: number | null;
      coverageRule: CoverageRule;
      /** Resolved from `controlPeriod`, the same way the farmer's endpoint does it. */
      requirementYears: number[];
      filterRule: FilterRule | null;
    }>;
  };
  share: {
    accessLevel: string;
    recipientEmail: string;
    expiresAt: string;
  };
  /** Answers to the control's form fields, keyed by field id. */
  formData: Record<string, any>;
  /**
   * IDs of filter-hidden requirements the farmer manually activated. Returned
   * at the top level of the response, NOT nested under `control`.
   */
  activatedRequirementIds: string[];
  /** IDs of filter-hidden form fields the farmer manually activated. */
  activatedFieldIds: string[];
  /**
   * The period the control assesses — same derivation as the lantbrukare
   * endpoint. Always present; estimated from today (flagged) without a date.
   */
  controlPeriod: ControlPeriod;
  /**
   * Documents attached to upload-enabled form fields. Same shape as
   * ControlDto.fieldDocuments so both views render from one source.
   */
  fieldDocuments: FieldDocument[];
  /**
   * Redacted farm profile — only the fields a FilterCondition can reference.
   * Lets the auditor view apply the same farm-profile filter the lantbrukare
   * sees. Null when the farm has no profile (nothing is then filtered out).
   */
  farmProfile: FilterableFarmProfile | null;
  /**
   * Affärspartners the control applies to — the registered organisations behind the
   * farm name in the "Delat av" block. Name and organisationsnummer only.
   *
   * Returned at the top level of the response, NOT nested under `control`.
   */
  affarspartners: Array<{ name: string; orgnr: string }>;
}

export class ShareService {
  /** Build headers with optional share session token. */
  private static shareHeaders(sessionToken?: string): HeadersInit {
    const headers: HeadersInit = {};
    if (sessionToken) headers['x-share-session'] = sessionToken;
    return headers;
  }

  /**
   * Create a new share
   */
  static async createShare(data: CreateShareRequest): Promise<Share> {
    const response = await apiClient.post<Share>('/shares', data);
    return response;
  }

  /**
   * Get all shares created by the authenticated user
   */
  static async getShares(): Promise<Share[]> {
    const response = await apiClient.get<Share[]>('/shares');
    return response;
  }

  /**
   * Get active shares for a specific control
   */
  static async getControlShares(controlId: string, includeExpired = false): Promise<Share[]> {
    const params = new URLSearchParams();
    if (includeExpired) {
      params.append('includeExpired', 'true');
    }
    const queryString = params.toString();
    const url = `/controls/${controlId}/shares${queryString ? `?${queryString}` : ''}`;
    const response = await apiClient.get<Share[]>(url);
    return response;
  }

  /**
   * Update share active status
   */
  static async updateShareStatus(shareId: string, isActive: boolean): Promise<Share> {
    const response = await apiClient.patch<Share>(`/shares/${shareId}/status`, { isActive });
    return response;
  }

  /**
   * Update share expiration time
   */
  static async updateShareExpiration(shareId: string, expiresInHours: number): Promise<Share> {
    const response = await apiClient.patch<Share>(`/shares/${shareId}`, { expiresInHours });
    return response;
  }

  /**
   * Delete a share
   */
  static async deleteShare(shareId: string): Promise<void> {
    await apiClient.delete(`/shares/${shareId}`);
  }

  /**
   * Verify share password and get session token
   */
  static async verifySharePassword(
    token: string,
    password: string
  ): Promise<{ valid: boolean; sessionToken?: string; message?: string; requiresPassword?: boolean }> {
    const response = await fetch(`${API_BASE_URL}/shares/public/${token}/verify-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to verify password');
    }

    const json = await response.json();
    // Backend wraps response in { success, data } structure
    return json.data || json;
  }

  /**
   * Access a shared control using public token (no authentication required)
   */
  static async accessPublicShare(token: string, sessionToken?: string): Promise<PublicShareData> {
    const response = await fetch(`${API_BASE_URL}/shares/public/${token}`, { headers: this.shareHeaders(sessionToken) });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to access shared control');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get share information without accessing the full control
   * Used to check if password is required before prompting user
   */
  static async getPublicShareInfo(token: string): Promise<{
    control: Share['control'];
    accessLevel: string;
    expiresAt: string;
    recipientEmail: string;
    requiresPassword: boolean;
  }> {
    const response = await fetch(`${API_BASE_URL}/shares/public/${token}/info`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Share not found or expired');
    }

    const data = await response.json();
    return data.data;
  }

  /**
   * Get view URL for a shared document (public access)
   */
  static async getDocumentViewUrl(shareToken: string, documentId: string, sessionToken?: string): Promise<{ viewUrl: string }> {
    const response = await fetch(`${API_BASE_URL}/shares/public/${shareToken}/documents/${documentId}/view-url`, { headers: this.shareHeaders(sessionToken) });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to get document view URL');
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Get convert URL for a shared Office document (public access)
   */
  static async getDocumentConvertUrl(shareToken: string, documentId: string, sessionToken?: string): Promise<{ convertUrl: string }> {
    const response = await fetch(`${API_BASE_URL}/shares/public/${shareToken}/documents/${documentId}/convert-url`, { headers: this.shareHeaders(sessionToken) });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to get document convert URL');
    }

    const data = await response.json();
    return data.data || data;
  }

  /**
   * Copy share URL to clipboard
   */
  static async copyShareUrl(shareUrl: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      return false;
    }
  }

  /**
   * Format access level for display
   */
  static formatAccessLevel(accessLevel: string): string {
    switch (accessLevel) {
      case 'VIEW':
        return 'Endast visning';
      case 'DOWNLOAD':
        return 'Visning & nedladdning';
      case 'COMMENT':
        return 'Visning, nedladdning & kommentarer';
      default:
        return accessLevel;
    }
  }

  /**
   * Check if share is expired
   */
  static isExpired(expiresAt: string): boolean {
    return new Date(expiresAt) < new Date();
  }

  /**
   * Format time until expiration
   */
  static formatTimeUntilExpiration(expiresAt: string): string {
    const now = new Date();
    const expiration = new Date(expiresAt);
    const diffMs = expiration.getTime() - now.getTime();

    if (diffMs <= 0) {
      return 'Utgången';
    }

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays} dag${diffDays !== 1 ? 'ar' : ''} kvar`;
    } else if (diffHours > 0) {
      return `${diffHours} timm${diffHours !== 1 ? 'ar' : 'e'} kvar`;
    } else {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minut${diffMinutes !== 1 ? 'er' : ''} kvar`;
    }
  }

  /**
   * Get access level options for select inputs
   */
  static getAccessLevelOptions() {
    return [
      { value: 'VIEW', label: 'Endast visning' },
      { value: 'DOWNLOAD', label: 'Visning & nedladdning' },
      { value: 'COMMENT', label: 'Visning, nedladdning & kommentarer' },
    ];
  }

  /**
   * Get expiration options in hours
   */
  static getExpirationOptions() {
    return [
      { value: 1, label: '1 timme' },
      { value: 6, label: '6 timmar' },
      { value: 24, label: '1 dag' },
      { value: 72, label: '3 dagar' },
      { value: 168, label: '1 vecka' },
    ];
  }
}